import {useEffect, useMemo, useState} from "preact/compat";
import {api} from "../../../../../services";
import {useUserContext, useBehavioralContext} from "../../../../../contexts";
import useGtmContext from "../../../../../contexts/GTMContext/gtmContext";

export const useAttendancePicker = props => {
    // Destructured props
    const {
        attendance,
        formKey,
        product,
        setOpenTermUse,
        categories,
        handleCurrentStep,
        sidebarCartData,
        handleCartLoader
    } = props;

    // An enum object containing all the available heading titles
    const HEADING_TITLES = {
        unity:         "Escolha uma de nossas unidades:",
        drivethru:     "Escolha um dos locais disponíveis:",
        home_delivery: "Digite seu CEP para o #VemSabin Atendimento Móvel:",
        airport:       "Escolha o aeroporto:",
        unidade_drive: "Escolha uma de nossas unidades:"
    }

    // The variable name already says it all
    const DEFAULT_SELECTED_AMOUNT = 1;

    // State to store the label of the current selected value
    const [selectedValue, setSelectedValue] = useState({
        id:       undefined,
        label:    undefined,
        stockQty: undefined,
        zip:      "",
        address:  ""
    });
    // Flags whether user tried to submit the form or not
    const [hasSubmitted, setHasSubmitted] = useState(false);
    // Flags if there is any invalid error in the form
    const [errors, setErrors] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    // State to control the loader for when the user adds an item to the cart
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    // Flag to info if freight is free
    const [isFreeFreight, setIsFreeFreight] = useState(false);

    // Memo to store the desired number of products. Defaults to 1
    const selectedAmount = useMemo(() => {
        return sidebarCartData.newProductData.selectedAmount || DEFAULT_SELECTED_AMOUNT;
    }, [sidebarCartData]);

    // eslint-disable-next-line no-unused-vars
    const [{}, {}, {cartData, setCartData, acceptedTermUse}] = useUserContext();

    // Retrieves the method to open the side cart component
    const {
        handleNewItemAlertStatus,
        handleCart
    } = useBehavioralContext();

    // Retrieves all the needed GTM methods
    const {handleAddProductEvent} = useGtmContext();

    const handleIsFreeFreight = (value) => {
        setIsFreeFreight(value);
    }

    // Method to handle the button label
    const handleSelectedValue = props => {
        // Destructured props
        const {data} = props;

        setSelectedValue(prevState => {
            return {
                ...prevState,
                ...data
            }
        });
    }

    const handleAddToCart = () => {
        // Calls the method to add item to cart
        addToCart().then();

        // Flags the form as submitted
        setHasSubmitted(true);
    }

    // Method to handle the form submission
    const handleSubmit = async e => {
        // Prevents page from reloading
        e.preventDefault();

        setHasSubmitted(true);

        // Validates form values
        // In this case, the only obligatory value is the ID,
        // as the label will only be used in the button component
        if (!selectedValue.id) {
            setErrors(true);
            setErrorMessage("Selecione uma opção para dar continuidade.");

            return;
        }

        // validates whether the sum of the quantity of items in the cart and the quantity requested is less than or equal to the available stock
        if (
            (
                cartData.qtyItems + selectedAmount
            ) > selectedValue.stockQty
        ) {
            setErrors(true);
            setErrorMessage("O PRODUTO NÃO ESTÁ DISPONÍVEL PARA A QUANTIDADE SOLICITADA.");

            return;
        }
        
        setErrors(false);
        setErrorMessage("");

        const isVaccine = categories.find((category) => {
            return category.urlKey === 'vacinas';
        });

        handleAddToCart();
        return;

        try {
            if (
                isVaccine &&
                !acceptedTermUse
            ) {
                setOpenTermUse(true);
            } else {
                handleAddToCart();
            }
        } catch (error) {
            console.error("Erro ao obter o carrinho atual:", error);
        }
    };

    // Handles any possible errors
    const handleErrors = (value) => {
        setErrors(value);
    }

    const getProductGTMData = useMemo(() => {
        const categoriesNameList = product.categories.map((item) => (item.name));
        const categoriesString = categoriesNameList.join(" | ");

        return {
            id: product.id || null,
            name: product.name || null,
            category: categoriesString || null,
            variant: selectedValue.label || null,
            brand: 'Sabin',
            quantity: selectedAmount || null,
            price: product.price || null,
        }
    }, [
        product,
        selectedValue,
        selectedAmount
    ])

    // Method to add the item to the cart
    const addToCart = async () => {
        try {
            // Activates the cart loader
            handleCartLoader(true);

            // Sets the button disabled state to true
            setIsAddingToCart(true);

            // The data to be sent in the payload
            const data = {
                formKey,
                product: product.id,
                stockId: selectedValue.id,
                amount: selectedAmount,
                ...(attendance.label === "home_delivery" && {
                    zipCode: selectedValue.zip
                })
            }

            // Tries to post data to the backend
            let response = await api.post("/v1/cart/add", data);

            // Sets the button disabled state to false
            setIsAddingToCart(false);

            // Checks if there was any error during the process
            if (response.data.error) {
                console.error("Error code:", response.data.code);

                setErrors(true);
                setErrorMessage(response.data.message);

                return;
            }

            setErrors(false);
            setErrorMessage("");

            // Retrieves the current warehouse
            const getRawWarehouse = () => {
                try {
                    // WHAT A HECK OF AN ALGORITHM IS THIS???
                    // TODO: OPTIMIZE THIS AS SOON AS POSSIBLE
                    return {
                        ...attendance.regions.find(region => {
                            return region.items.find(item => {
                                return selectedValue.id ?
                                    item.id === selectedValue.id :
                                    item.label.includes(selectedValue.label);
                            });
                        }).items.find(item => {
                            return selectedValue.id ?
                                item.id === selectedValue.id :
                                item.label.includes(selectedValue.label);
                        }),
                        ...(attendance.label === "home_delivery" && {
                            zipCode: selectedValue.zip,
                            address: selectedValue.address,
                        })
                    };
                } catch (e) {
                    console.warn("Bad algorithm execution:", e);

                    return {};
                }
            }

            // Synchronizes the "lastWarehouseAdded" property from the sidebarCartData
            // state at the behavioral context with the last added warehouse.
            handleCart("lastWarehouseAdded", {
                type: attendance.label,
                data: getRawWarehouse()
            });

            // Updates the cart items amount
            setCartData(prevState => {
                return {
                    ...prevState,
                    qtyItems: response.data.qtyItems
                }
            })

            handleAddProductEvent(getProductGTMData);

            // Proceeds to the cart resume step
            handleCurrentStep("resume");
        } catch (err) {
            console.error("Error while adding item to cart:", err);

            // Deactivates the cart loader in case of failure
            handleCartLoader(false);
        }
    }

    useEffect(() => {
        if (acceptedTermUse && hasSubmitted) {
            setOpenTermUse(false);

            handleAddToCart();
        }
    }, [acceptedTermUse]);

    return {
        HEADING_TITLES,
        selectedAmount,
        handleSubmit,
        selectedValue,
        errors,
        handleSelectedValue,
        hasSubmitted,
        handleErrors,
        isAddingToCart,
        isFreeFreight,
        handleIsFreeFreight,
        errorMessage,
    }
}
